'use client';

import { useEffect, useRef } from 'react';

interface EditableTextProps {
  value: string;
  onChange?: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  as?: 'p' | 'h1' | 'h2' | 'h3' | 'span' | 'li' | 'div';
  multiline?: boolean;
  placeholder?: string;
}

/**
 * Click-to-edit text for the deck editor. When `onChange` is provided the
 * element becomes contentEditable and emits on blur. When omitted it
 * renders as plain text.
 *
 * We intentionally treat the DOM as the source of truth during an edit
 * session; React only pushes `value` into the node when the element is not
 * currently focused, so typing doesn't fight the caret.
 */
export function EditableText({
  value,
  onChange,
  className,
  style,
  as = 'p',
  multiline = false,
  placeholder,
}: EditableTextProps) {
  const ref = useRef<HTMLElement | null>(null);

  const editable = Boolean(onChange);

  useEffect(() => {
    if (!editable) return;
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.innerText !== value) {
      el.innerText = value;
    }
  }, [value, editable]);

  const Tag = as as keyof React.JSX.IntrinsicElements;

  /**
   * Read-only (e.g. Playwright PDF): render `value` as real React children so
   * text survives reconciliation. Never rely on useEffect + innerText alone —
   * a parent re-render clears empty-host children and leaves only the bg.
   */
  if (!editable) {
    const readOnlyClass = `${className ?? ''} ${multiline ? 'whitespace-pre-wrap' : ''}`.trim();
    return (
      <Tag className={readOnlyClass || undefined} style={style}>
        {value}
      </Tag>
    );
  }

  const props = {
    ref,
    style,
    className:
      `${className ?? ''} deck-editable outline-none focus:rounded-md focus:bg-white/10 focus:ring-2 focus:ring-marine-green/40`.trim(),
    contentEditable: true as const,
    suppressContentEditableWarning: true,
    'data-placeholder': placeholder,
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      onChange?.(e.currentTarget.innerText);
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      if (!multiline && e.key === 'Enter') {
        e.preventDefault();
        (e.currentTarget as HTMLElement).blur();
      }
    },
    onPaste: (e: React.ClipboardEvent<HTMLElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
    },
  } as React.HTMLAttributes<HTMLElement> & { ref: typeof ref };

  return (
    // Tag is a union of intrinsics; narrow handler types for TS.
    // @ts-expect-error — ref + contentEditable union on intrinsic `Tag`.
    <Tag {...props} />
  );
}
